// RU-PNLTAB: realized P&L tree (segment → type → symbol → trades) from GET /api/analytics/pnl-breakdown.
// Shared by the P&L page and the P&L Calendar's day detail (from = to = the day).
import { useEffect, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

export const breakdownUrl = (mode, from, to) =>
  `/api/analytics/pnl-breakdown?mode=${encodeURIComponent(mode)}&from=${from}&to=${to}`;

export const fmtInr = (n) =>
  n == null ? "—" : `${n >= 0 ? "+" : "-"}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const pnlColor = (n) => (n > 0 ? "text-emerald-400" : n < 0 ? "text-red-400" : "text-zinc-400");

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

function Stats({ node }) {
  return (
    <span className="flex items-center gap-3 shrink-0 text-[11px] font-mono">
      <span className="text-zinc-500 w-16 text-right" data-testid="row-trades">{node.trades} {node.trades === 1 ? "trade" : "trades"}</span>
      <span className="text-zinc-500 w-14 text-right" data-testid="row-winrate">{node.win_rate_pct}% win</span>
      <span className={`w-24 text-right font-semibold ${pnlColor(node.pnl)}`} data-testid="row-pnl">{fmtInr(node.pnl)}</span>
    </span>
  );
}

function Row({ depth, label, node, open, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={testid}
      className="w-full flex items-center justify-between gap-2 py-2 pr-2 hover:bg-zinc-800/50 text-left border-b border-zinc-800/60"
      style={{ paddingLeft: 8 + depth * 16 }}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
        <span className={`truncate ${depth === 0 ? "text-xs font-semibold text-zinc-100 uppercase tracking-wider" : depth === 1 ? "text-xs text-zinc-300" : "text-xs font-mono text-zinc-200"}`}>
          {label}
        </span>
      </span>
      <Stats node={node} />
    </button>
  );
}

function TradeLine({ t }) {
  const label = t.type === "OPT" && t.option_strike ? `${t.symbol} ${t.option_strike}${t.option_kind || ""}` : t.symbol;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 pr-2 border-b border-zinc-800/40 text-[11px] font-mono"
         style={{ paddingLeft: 8 + 3 * 16 }} data-testid="trade-row">
      <span className="flex items-center gap-2 min-w-0 flex-wrap text-zinc-400">
        <span className="text-zinc-300">{label}</span>
        <span className={t.direction === "SHORT" ? "text-red-400" : "text-emerald-400"}>{t.direction}</span>
        <span>Qty {t.qty}</span>
        <span>₹{t.entry_price} → ₹{t.exit_price}</span>
        <span className="text-zinc-600">{fmtTime(t.exit_at)}</span>
        {t.pnl_estimated ? <span className="text-amber-500">est.</span> : null}
      </span>
      <span className={`shrink-0 font-semibold ${pnlColor(t.pnl)}`}>{fmtInr(t.pnl)}</span>
    </div>
  );
}

export function BreakdownTree({ realized }) {
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  if (!realized || !realized.segments || realized.segments.length === 0) {
    return <div className="py-6 text-center text-xs text-zinc-600" data-testid="tree-empty">No closed trades in this period</div>;
  }
  return (
    <div data-testid="pnl-tree">
      {realized.segments.map((s) => {
        const sk = s.key;
        return (
          <div key={sk}>
            <Row depth={0} label={s.label} node={s} open={!!open[sk]} onToggle={() => toggle(sk)} testid={`seg-${sk}`} />
            {open[sk] && s.types.map((ty) => {
              const tk = `${sk}/${ty.key}`;
              return (
                <div key={tk}>
                  <Row depth={1} label={ty.label} node={ty} open={!!open[tk]} onToggle={() => toggle(tk)} testid={`type-${tk}`} />
                  {open[tk] && ty.symbols.map((sy) => {
                    const yk = `${tk}/${sy.symbol}`;
                    return (
                      <div key={yk}>
                        <Row depth={2} label={sy.symbol} node={sy} open={!!open[yk]} onToggle={() => toggle(yk)} testid={`sym-${yk}`} />
                        {open[yk] && sy.items.map((t, i) => <TradeLine key={t.id || i} t={t} />)}
                        {open[yk] && sy.items_truncated && (
                          <div className="py-1.5 pr-2 text-[11px] text-zinc-500 border-b border-zinc-800/40"
                               style={{ paddingLeft: 8 + 3 * 16 }} data-testid="items-capped">
                            Showing the latest {sy.items.length} of {sy.trades} trades (totals include all)
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function OpenBlock({ open, paperPrefix }) {
  if (!open) return null;
  return (
    <div className="border border-zinc-800 rounded-sm" data-testid="open-block">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="open-label">{paperPrefix ? `Paper · ${open.label}` : open.label}</span>
        <span className={`text-xs font-mono font-semibold ${pnlColor(open.pnl)}`}>{fmtInr(open.pnl)}</span>
      </div>
      {open.positions.length === 0 ? (
        <div className="px-3 py-2 text-[11px] text-zinc-600">No open positions</div>
      ) : open.positions.map((p, i) => (
        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-[11px] font-mono border-b border-zinc-800/40 last:border-0">
          <span className="text-zinc-300">{p.symbol} <span className="text-zinc-600">{p.type}</span> <span className="text-zinc-500">Qty {p.qty}</span></span>
          <span className={pnlColor(p.pnl)}>{fmtInr(p.pnl)}</span>
        </div>
      ))}
    </div>
  );
}

function LiveNote({ live }) {
  if (!live) return null;
  return (
    <div className="border border-amber-900/50 bg-amber-950/20 rounded-sm px-3 py-2 text-[11px] text-amber-300/90 space-y-1" data-testid="live-note">
      <div>{live.note}</div>
      {live.broker_reported_pnl != null && (
        <div className="font-mono">
          {live.broker_reported_label}: <span className={pnlColor(live.broker_reported_pnl)}>{fmtInr(live.broker_reported_pnl)}</span>
        </div>
      )}
    </div>
  );
}

// Fetches and renders the breakdown for (mode, from, to). `compact` hides the summary strip (day detail has its own).
export default function PnLBreakdownTree({ mode, from, to, compact = false }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    setData(null);
    fetch(breakdownUrl(mode, from, to), { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) { setData(d); setState("ok"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, [mode, from, to]);

  if (state === "loading") return <div className="py-6 text-center text-xs text-zinc-600 animate-pulse">Loading P&L…</div>;
  if (state === "error" || !data) return <div className="py-6 text-center text-xs text-zinc-600" data-testid="tree-error">Failed to load P&L breakdown</div>;

  const r = data.realized;
  // PT-11: live has no realized ledger yet; show "Not available", never ₹0 / 0 trades.
  const unavailable = data.mode === "live" && data.live && data.live.realized_available === false;
  // PT-11: in Combined the realized tree and the open block are the paper book only.
  const paperOnly = data.mode === "combined";
  const na = "Not available";
  const muted = "text-zinc-500";
  return (
    <div className="space-y-3" data-testid="pnl-breakdown">
      {data.truncated && (
        <div className="border border-red-900/60 bg-red-950/30 rounded-sm px-3 py-2 text-[11px] text-red-300" data-testid="truncated-warning">
          More than {Number(data.row_cap).toLocaleString("en-IN")} trades in this period: totals cover only the first{" "}
          {Number(data.row_cap).toLocaleString("en-IN")} trades. Narrow the date range for exact figures.
        </div>
      )}
      {!compact && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: paperOnly ? "Paper realized P&L" : "Realized P&L", value: unavailable ? na : fmtInr(r.pnl), color: unavailable ? muted : pnlColor(r.pnl), testid: "sum-pnl" },
            { label: "Closed trades", value: unavailable ? na : r.trades, color: unavailable ? muted : "text-zinc-200", testid: "sum-trades" },
            { label: "Win rate", value: unavailable ? na : `${r.win_rate_pct}%`, color: unavailable ? muted : "text-zinc-200", testid: "sum-winrate" },
            { label: "Wins / Losses", value: unavailable ? na : `${r.wins} / ${r.losses}`, color: unavailable ? muted : "text-zinc-200", testid: "sum-wl" },
          ].map(({ label, value, color, testid }) => (
            <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
              <div className={`text-lg font-mono font-semibold ${color}`} data-testid={testid}>{value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="realized-label">
          {paperOnly ? "Paper realized" : "Realized"} (closed trades{data.fees_included ? "" : ", before charges"})
        </div>
        {unavailable ? (
          <div className="py-6 text-center text-xs text-zinc-500" data-testid="realized-unavailable">
            Not available: realized P&L by trade is not available for live
          </div>
        ) : (
          <BreakdownTree realized={r} />
        )}
      </div>
      {data.unbooked && data.unbooked.count > 0 && (
        <div className="text-[11px] text-zinc-500" data-testid="unbooked">
          {data.unbooked.count} closed {data.unbooked.count === 1 ? "trade has" : "trades have"} no realized P&L (bad average price) and {data.unbooked.count === 1 ? "is" : "are"} not counted.
        </div>
      )}
      <OpenBlock open={data.open} paperPrefix={paperOnly} />
      <LiveNote live={data.live} />
    </div>
  );
}
